import { useEffect, useState, type ReactNode } from 'react';
import type { DebriefView } from '@nightshift/engine/client';
import { api } from '../net/api.ts';
import { ROLE_TITLE } from '../ui/bits.tsx';
import { AXES } from './Debrief.tsx';
import type { Timeline } from './wire.ts';
import '../styles/debrief-print.css';

interface PrintSummary {
  name: string;
  preset: string;
  createdAt: number;
  endedAt: number | null;
  layers: Record<'staff' | 'vents' | 'blood', boolean>;
  debrief: DebriefView | null;
}

/** Opens the faculty debrief as printable table sheets. */
export function DebriefPrint({ sessionId, table }: { sessionId: string; table: string | null }): ReactNode {
  const [data, setData] = useState<{ summary: PrintSummary; timeline: Timeline } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setData(null);
    setError(null);
    Promise.all([api<PrintSummary>(`/api/sessions/${sessionId}`), api<Timeline>(`/api/sessions/${sessionId}/timeline?min=2`)])
      .then(([summary, timeline]) => { if (active) setData({ summary, timeline }); })
      .catch((err: Error) => {
        console.warn('Printable debrief unavailable:', sessionId, err);
        if (active) setError(err.message);
      });
    return () => { active = false; };
  }, [sessionId]);
  const tables = data?.summary.debrief?.tables.filter((row) => !table || table === 'all' || String(row.no) === table) ?? [];
  const message = error ?? (data && !data.summary.debrief ? 'The debrief is available after the shift ends.' : data && !tables.length ? 'No such table in this session.' : null);
  useEffect(() => {
    if (!data || message || !tables.length) return;
    let active = true;
    void document.fonts.ready.then(() => { if (active) window.print(); }).catch((err: Error) => {
      console.warn('Debrief print dialog unavailable:', sessionId, err);
      if (active) setError('The print dialog could not open. Use your browser’s Print command.');
    });
    return () => { active = false; };
  }, [data, message, table]);
  return <main className="debrief-print">
    <nav className="print-controls" aria-label="Print controls">
      <a href={`/control/s/${sessionId}`}>Back to session</a>
      <a href={`/control/s/${sessionId}/replay`}>Saved timeline</a>
      <button disabled={!data || !!message} onClick={() => window.print()}>Print</button>
    </nav>
    {message ? <p role="alert">{message}</p> : !data ? <p>Loading the debrief…</p> : tables.map((row) => <PrintTable key={row.no} summary={data.summary} table={row} timeline={data.timeline} />)}
  </main>;
}

/** Renders one table's debrief on a single sheet. */
export function PrintTable({ summary, table, timeline }: { summary: PrintSummary; table: DebriefView['tables'][number]; timeline: Timeline }): ReactNode {
  const highlights = timeline.events.filter((event) => (event.table === null || event.table === table.no) && event.severity >= 2).slice(-6);
  const notes = timeline.notes.filter((note) => note.starred && (note.table_no === null || note.table_no === table.no));
  return <article className="print-sheet" data-testid="print-sheet" data-table={table.no}>
    <header>
      <h1>{summary.name} · Table {table.no}</h1>
      <p>{summary.preset} · {new Date(summary.endedAt ?? summary.createdAt).toLocaleDateString('en-AU', { dateStyle: 'long' })}</p>
      <p>Final Lead: {ROLE_TITLE[table.lead.role]}{table.lead.name ? ` (${table.lead.name})` : ''} · Day consultant: {table.dayConsultant}</p>
    </header>
    <div className="print-grid">
      <div>
        <h2>Dashboard</h2>
        <table className="print-metrics"><tbody>
          {AXES.filter((axis) => !axis.layer || summary.layers[axis.layer]).map((axis) => <tr key={axis.k} data-metric={axis.k}><th scope="row">{axis.label}</th><td>{table.dashboard[axis.k] ?? '—'}</td></tr>)}
        </tbody></table>
        <h2>Calibration · this table</h2>
        <table><thead><tr><th>Printed</th><th>Drawn, right</th><th>Deck, right</th></tr></thead><tbody>
          {table.calibration.map((row) => <tr key={row.box}><th scope="row">{row.box}%</th><td>{row.drawnRight} of {row.drawn}</td><td>{row.deckRight} of {row.deck}</td></tr>)}
        </tbody></table>
        <h2>Hand-written notes</h2>
        <div className="print-notes-box" aria-label="Space for hand-written notes"><hr /><hr /><hr /><hr /><hr /></div>
      </div>
      <div>
        <h2>Objectives</h2>
        <ul>{table.objectives.map((objective) => <li key={objective.role}><b>{ROLE_TITLE[objective.role]}: {objective.title}</b> — {objective.achieved ? 'met' : 'not met'}. {objective.detail}</li>)}</ul>
        <h2>Ask the table</h2>
        <ul>{table.prompts.map((prompt, i) => <li key={i}>{prompt}</li>)}</ul>
        <h2>Highlights · up to six excerpts</h2>
        <ul>{highlights.map((event, i) => <li className="print-excerpt" key={i}>Turn {event.turn}: {event.text.length > 120 ? `${event.text.slice(0, 117)}…` : event.text}</li>)}</ul>
        <h2>Starred notes · excerpts</h2>
        {notes.length ? <ul>{notes.slice(0, 6).map((note) => <li className="print-excerpt" key={note.id}>{note.text.length > 120 ? `${note.text.slice(0, 117)}…` : note.text}</li>)}</ul> : <p>No starred notes.</p>}
        {notes.length > 6 && <p>{notes.length - 6} more starred notes in the saved timeline.</p>}
        <p className="print-note-source">Excerpts; full starred notes are in the saved timeline.</p>
      </div>
    </div>
  </article>;
}
